/**
 * WarehouseTopItemsPanel
 * /api/warehouse/analytics/top_used_items/?limit=10
 * Shows top-issued items as horizontal progress bars with key stats.
 */
import { getAccessToken } from '../../../auth/tokenStore';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, RefreshCw, Loader2, Package } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const fmtVal = n => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
  return v.toFixed(0);
};

const COLORS = ['#F97316','#3B82F6','#10B981','#8B5CF6','#EC4899','#F59E0B','#14B8A6','#EF4444','#6366F1','#84CC16'];

const WarehouseTopItemsPanel = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${getAccessToken()}` };
    axios.get(`${API_BASE}/api/warehouse/analytics/top_used_items/`, {
      headers, params: { limit: 10 },
    })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Could not load top items.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  const maxIssued = items[0]?.total_issued || 1;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <BarChart3 size={13} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-800">Top Used Items</span>
          <span className="text-[10px] text-slate-400">by issue frequency</span>
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
          <Package size={28} />
          <span className="text-xs">No issue history yet</span>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* column headers */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-steel-100 bg-steel-50 sticky top-0 z-10">
            <span className="flex-1 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Item</span>
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide w-24 text-center">Issue frequency</span>
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide w-12 text-right">Issued</span>
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide w-10 text-right">Stock</span>
          </div>

          {items.map((item, i) => {
            const pct   = Math.round((item.total_issued / maxIssued) * 100);
            const color = COLORS[i % COLORS.length];
            const stockLow = item.current_stock !== null && item.current_stock <= 0;
            return (
              <div key={item.item_id}
                className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                {/* rank + name */}
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="text-[9px] font-bold text-slate-300 w-4 flex-shrink-0">#{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-slate-700 truncate">{item.item_name}</p>
                    {item.sku && <p className="text-[9px] text-slate-400">{item.sku}</p>}
                  </div>
                </div>

                {/* frequency bar */}
                <div className="w-24 flex items-center gap-1.5 flex-shrink-0">
                  <div className="flex-1 h-1.5 bg-steel-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full"
                      style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                  <span className="text-[9px] text-slate-400 w-6 text-right">{pct}%</span>
                </div>

                {/* total issued */}
                <span className="text-[11px] font-bold text-slate-700 w-12 text-right">
                  {fmtVal(item.total_issued)}
                  {item.total_value_issued > 0 && (
                    <span className="block text-[9px] font-normal text-slate-400">{fmtVal(item.total_value_issued)}</span>
                  )}
                </span>

                {/* current stock */}
                <span className="text-[11px] font-bold w-10 text-right"
                  style={{ color: stockLow ? '#EF4444' : '#64748b' }}>
                  {item.current_stock ?? '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WarehouseTopItemsPanel;
